import r from 'structure-driver'
import TokenService from 'structure-token-service'
import RootModel from 'structure-root-model'
import ApplicationSecretModel from './application-secret'

/**
 * ApplicationModel Class
 *
 * @public
 * @class ApplicationModel
 */
export default class ApplicationModel extends RootModel {

  /**
   * ApplicationModel constructor
   *
   * @public
   * @constructor
   * @param {Object} options - Options
   */
  constructor(options = {}) {
    super(Object.assign({}, {
      table: 'applications',
      relations: {
        belongsTo: [
          {
            node: 'organizations',
            link: {
              foreignKey: 'organizationId',
              localKey: 'applicationId'
            },
            joinTable: 'link_organizations_applications',
            strip: false
          }
        ],
        hasMany: [
          {
            node: 'users',
            link: {
              foreignKey: 'userId',
              localKey: 'applicationId'
            },
            joinTable: 'link_applications_users'
          }
        ]
      }
    }, options))
  }

  /**
   * Create application
   *
   * @public
   * @param {Object} pkg
   */
  async create(pkg = {}, options = {}) {

    const applicationSecret = new ApplicationSecretModel({
      logger: this.logger,
      organizationId: pkg.organizationId
    })

    if(pkg.hostname) {

      const hostname = Object.assign({}, pkg).hostname
      delete pkg.hostname
      pkg.hostnames = [hostname]

    }

    const res = await RootModel.prototype.create.call(this, pkg, options)

    const secretPkg = {
      applicationId: res.id,
      secret: await this.generateSecret(),
    }

    await applicationSecret.create(secretPkg)

    return res

  }

  /**
   * Create an application secret
   *
   * @public
   */
  generateSecret() {
    return new TokenService().issue(`${Date.now()}`)
  }

  /**
   * Get application by hostname
   *
   * @public
   * @param {String} hostname
   */
  getByHostname(hostname, organizationId) {

    return new Promise( async (resolve, reject) => {

      try {
        let apps = []

        let query = r
          .table(this.table)
          .getAll(hostname, {index: 'hostnames'})

        if(organizationId) {
          query = query
            .eqJoin('id', r.table('link_organizations_applications'), {index: 'applicationId'})
            .eqJoin(r.row('right')('organizationId'), r.table('organizations'), {index: 'id'})
            .filter(function(doc) {
              return doc('right')('id').eq(organizationId)
            })

          apps = await query.run()

          if(apps.length > 0) return resolve(apps[0].left.left)

        } else {

          apps = await query.run()

          if(apps.length > 0) return resolve(apps[0])
        }

        resolve()

      } catch(e) {

        this.logger.error('Error getting application by hostname', e)

        reject(e)
      }

    })

  }

  /**
   * Get organization
   *
   * @public
   * @param {String} id
   */
  getOrganization(id) {

    return new Promise( async (resolve, reject) => {

      try {

        const res = await r
          .table(this.table)
          .getAll(id)
          .concatMap((app) => {
            return r
              .table('organizations')
              .getAll(app('organizationId'))
          })
          .limit(1)

        let organization = false

        if(res.length > 0) {
          organization = res[0]
        }

        resolve(organization)

      } catch(e) {

        this.logger.error('Error getting organization of application', e)

        reject(e)
      }

    })

  }

  ofUser(id) {

    return new Promise( async (resolve, reject) => {

      try {

        const res = await r
          .table('users')
          .getAll(id)
          .concatMap((user) => {
            return r
              .table('link_applications_users')
              .getAll(user('id'), {index: 'userId'})
          })
          .concatMap((link) => {
            return r
              .table('applications')
              .getAll(link('applicationId'))
              .filter((app) => {
                return app('status').eq('deleted').not()
              })
          })

        resolve(res)

      } catch(e) {

        this.logger.error('Error getting users of application', e)

        reject(e)
      }

    })

  }

  /**
   * Update application by id
   *
   * @public
   * @param {Object} pkg
   */
  updateById(id, pkg = {}) {

    delete pkg.secret

    return new Promise( async (resolve, reject) => {

      try {

        const app = await RootModel.prototype.updateById.call(this, id, pkg)

        resolve(app)

      } catch(e) {

        this.logger.error(e)

        reject(e)
      }

    })

  }

  /**
   * Reference a user to an application
   *
   * @param {Object} pkg - The options
   */
  createReference(pkg = {}) {

    return RootModel.prototype.createReference.call(this, pkg, {table: 'link_applications_users'})

  }

  /**
   * Unreference a user to an application
   *
   * @param {Object} pkg - The options
   */
  deleteReference(pkg = {}) {

    return RootModel.prototype.deleteReference.call(this, pkg, {table: 'link_applications_users'})

  }

}
